import React from "react";

import { IgChip, IgTooltip } from "@intelligems/ig-theme";
import {
  AudienceDto,
  EXPERIENCE_AUDIENCE_ACTION_OPTIONS,
} from "@intelligems/ig-types";
import { Grid } from "@mui/material";
import { formatQueries } from "@src/components/targeting/components/audienceTargeting/utils";
import { renderConditionText } from "@src/features/experiments/utils/validators";
import { TempTargetingTooltip } from "@src/features/sharedExperience/components/targeting/TempTargetingTooltip";
import { Experience } from "@src/store/slices/experiences/types";

interface SharedTargetingChipProps {
  audienceState: AudienceDto | undefined | null;
  experience: Experience;
  onPageTargetingChipClick?: () => void;
  onLinkChipClick?: () => void;
  chipColor?: "info" | "error" | "warning" | "success" | "accent" | "default";
  hideTooltip?: boolean;
  copyClicked?: boolean;
  onGeneralChipClick?: () => void;
}

export const PageTargetingChip = ({
  experience,
  onPageTargetingChipClick,
  chipColor,
  audienceState,
  hideTooltip,
  onGeneralChipClick,
}: SharedTargetingChipProps) => {
  return (
    <IgTooltip
      title={
        hideTooltip ? (
          ""
        ) : (
          <>
            {"Modifications limited to certain pages"}
            {experience.category === "personalization" && (
              <TempTargetingTooltip audienceState={audienceState} />
            )}
          </>
        )
      }
    >
      <IgChip
        onClick={() => {
          if (onGeneralChipClick) onGeneralChipClick();
          if (onPageTargetingChipClick) onPageTargetingChipClick();
        }}
        clickable={
          onGeneralChipClick !== undefined ||
          onPageTargetingChipClick !== undefined
        }
        color={chipColor || "error"}
        key={"page"}
        label={"Page"}
      />
    </IgTooltip>
  );
};

export const LinkTargetingChip = ({
  experience,
  chipColor,
  audienceState,
  hideTooltip,
  onLinkChipClick,
  copyClicked,
  onGeneralChipClick,
}: SharedTargetingChipProps) => {
  return (
    <IgTooltip
      title={
        hideTooltip ? (
          ""
        ) : copyClicked ? (
          "Link Copied"
        ) : (
          <>
            Shown only to visitors with a special link.{" "}
            {experience.id !== undefined ? "Click to copy link." : ""}
            {experience.category === "personalization" && (
              <TempTargetingTooltip audienceState={audienceState} />
            )}
          </>
        )
      }
    >
      <IgChip
        onClick={() => {
          if (onGeneralChipClick) onGeneralChipClick();
          if (onLinkChipClick) void onLinkChipClick();
        }}
        color={chipColor || "warning"}
        key={"link"}
        label={"Link"}
        clickable={Boolean(experience.id) || onGeneralChipClick !== undefined}
      />
    </IgTooltip>
  );
};

export const AllVisitorsChip = ({
  chipColor,
  hideTooltip,
  onGeneralChipClick,
}: SharedTargetingChipProps) => {
  return (
    <IgTooltip title={hideTooltip ? "" : "Shown to all visitors"}>
      <IgChip
        onClick={onGeneralChipClick}
        clickable={onGeneralChipClick !== undefined}
        color={chipColor || "default"}
        key={"all"}
        label={"All Visitors"}
      />
    </IgTooltip>
  );
};

export const AdvancedTargetingChip = ({
  chipColor,
  audienceState,
  onGeneralChipClick,
}: SharedTargetingChipProps) => {
  return (
    <IgTooltip
      title={
        <>
          {audienceState!.filters.map((audFilter, index) => (
            <React.Fragment key={index}>
              If {renderConditionText(audFilter.expression!)} then{" "}
              {audFilter.action === "leaveUnassigned"
                ? EXPERIENCE_AUDIENCE_ACTION_OPTIONS["experienceUnassigned"]
                    .title
                : EXPERIENCE_AUDIENCE_ACTION_OPTIONS[
                    audFilter.action || "experienceUnassigned"
                  ].title}
              <br />
            </React.Fragment>
          ))}
        </>
      }
    >
      <IgChip
        onClick={onGeneralChipClick}
        clickable={onGeneralChipClick !== undefined}
        color={chipColor || "accent"}
        label={"Advanced Audience"}
      />
    </IgTooltip>
  );
};

export const CurrencyTargetingChip = ({
  experience,
  chipColor,
  audienceState,
  hideTooltip,
  onGeneralChipClick,
}: SharedTargetingChipProps) => {
  return (
    <IgTooltip
      title={
        hideTooltip ? (
          ""
        ) : (
          <>
            {`Shown only to visitors in: ${audienceState!.excludeCurrency.currency
              ?.split(",")
              .join(", ")}`}
            {experience.category === "personalization" && (
              <TempTargetingTooltip audienceState={audienceState} />
            )}
          </>
        )
      }
    >
      <IgChip
        onClick={onGeneralChipClick}
        clickable={onGeneralChipClick !== undefined}
        color={chipColor || "info"}
        label={audienceState!.excludeCurrency.currency?.split(",").join(", ")}
      />
    </IgTooltip>
  );
};
